import React, { useMemo } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Play, Clock, Target, MessageCircle, Award, Phone, PhoneOff, Mic, MicOff, FileText, Send, Lock } from "lucide-react";
import { CheckCircle, Microphone } from "@phosphor-icons/react";
import type { AIAgent, AgentPresentation } from "../types";
import { useTranslation } from "react-i18next";
import { pickSimulatorDescription, pickSimulatorPresentation, normalizeAgentPresentation } from "../lib/simulator-description-i18n";

// Safe HTML renderer — only allows <strong> tags from i18n translations
function SafeHtml({ html, className }: { html: string; className?: string }) {
  const safe = html
    .replace(/<(?!\/?strong\b)[^>]*>/gi, "")        // strip all tags except <strong>
    .replace(/<strong\s[^>]*>/gi, "<strong>");       // strip attributes from strong tags
  return <span className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
}

interface AgentPresentationPageProps {
  agent: AIAgent;
  onBack: () => void;
  onStartTraining: () => void;
}

export function AgentPresentationPage({ agent, onBack, onStartTraining }: AgentPresentationPageProps) {
  const { t, i18n } = useTranslation();
  // Lot 7 i18n — description du simulateur résolue selon la langue active
  // (descriptionFr/En avec fallback sur le champ legacy `description`).
  const localizedDescription = pickSimulatorDescription(agent, i18n.language);
  // Lot 8 i18n — presentationJson résolu selon la langue active
  // (presentationJsonFr/En avec fallback sur le champ legacy `presentationJson`).
  const details: AgentPresentation = useMemo(() => {
    // Le presentationJson stocké peut être partiel (skills/steps/tips manquants) → on
    // normalise pour garantir des tableaux et éviter le crash .length/.map (BIZCOACH-R/Q).
    const parsed = pickSimulatorPresentation<Partial<AgentPresentation>>(agent, i18n.language);
    return normalizeAgentPresentation(parsed, localizedDescription);
  }, [agent.presentationJson, agent.presentationJsonFr, agent.presentationJsonEn, i18n.language, localizedDescription]);

  const agentColor = agent.color || "#988EF2";

  return (
    <div className="space-y-1 md:space-y-1.5 max-w-7xl mx-auto md:-mt-3">
      {/* Header */}
      <h1 className="text-base md:text-lg font-bold" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
        {t("agentPresentation.title")}
      </h1>

      {/* Card principale - Présentation de l'agent */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-3 pt-0 pb-2 md:px-6 md:pt-4 md:pb-3">
          {/* Mobile : bouton Jouer en haut à droite */}
          <div className="flex justify-end md:hidden mb-0.5">
            {agent.isUnlocked !== false ? (
            <Button
              size="lg"
              className="px-6 py-5 text-base font-semibold gap-2"
              style={{ backgroundColor: '#F99002' }}
              onClick={onStartTraining}
            >
              <Play className="h-5 w-5" />
              {agent.simulatorType === "coach" ? t("agentPresentation.join") : t("agentPresentation.play")}
            </Button>
            ) : (
            <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm text-center">
              <Lock className="h-4 w-4 inline mr-1.5" />
              {t("agentPresentation.licenceRequired")}
            </div>
            )}
          </div>
          {/* Mobile : icône + nom */}
          <div className="flex items-center gap-2 mb-2 md:hidden">
            <div className="flex-shrink-0">
              {agent.image ? (
                <img src={agent.image} alt={agent.name} loading="lazy" className="h-10 w-10 rounded-full object-cover" style={{ objectPosition: 'top' }} />
              ) : (
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-lg"
                  style={{ backgroundColor: agentColor, color: 'white' }}
                >
                  {agent.icon}
                </div>
              )}
            </div>
            <h2 className="text-lg font-bold" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
              {agent.name}
            </h2>
          </div>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
            {/* Icône de l'agent (desktop) */}
            <div className="flex-shrink-0 hidden md:block">
              {agent.image ? (
                <img src={agent.image} alt={agent.name} loading="lazy" className="h-20 w-20 rounded-full object-cover" style={{ objectPosition: 'top' }} />
              ) : (
                <div
                  className="h-20 w-20 rounded-full flex items-center justify-center text-3xl"
                  style={{ backgroundColor: agentColor, color: 'white' }}
                >
                  {agent.icon}
                </div>
              )}
            </div>

            {/* Infos de l'agent */}
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold mb-1 hidden md:block" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
                {agent.name}
              </h2>
              <p className="text-sm md:text-base mb-3 md:mb-3" style={{ color: '#6B7A99' }}>
                {details.fullDescription}
              </p>

              {/* Badges d'info */}
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                {details.objective && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full bg-white shadow-sm">
                    <Target className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" style={{ color: '#F99002' }} />
                    <span className="text-xs md:text-sm font-medium whitespace-nowrap" style={{ color: '#384872' }}>{details.objective}</span>
                  </div>
                )}
                {details.duration && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full bg-white shadow-sm">
                    <Clock className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" style={{ color: '#72B76E' }} />
                    <span className="text-xs md:text-sm font-medium whitespace-nowrap" style={{ color: '#384872' }}>{details.duration}</span>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        <CardContent className="p-3 pt-1.5 md:px-6 md:pt-1.5 md:pb-4">
          {/* Compétences travaillées */}
          {details.skills.length > 0 && (
            <div className="mb-4 md:mb-4">
              <h3 className="text-lg font-semibold mb-2 md:mb-3 flex items-center gap-2" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
                <Award className="h-5 w-5" style={{ color: '#F6C847' }} />
                {t("agentPresentation.skillsWorked")}
              </h3>
              <div className="flex flex-wrap gap-2">
                {details.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-full text-sm font-medium"
                    style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Déroulement de la simulation */}
          {details.steps.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 md:mb-3 flex items-center gap-2" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
                <MessageCircle className="h-5 w-5" style={{ color: '#3B82F6' }} />
                {t("agentPresentation.simulationSteps")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {details.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-xl bg-slate-50"
                  >
                    <div
                      className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-white font-bold text-xs"
                      style={{ backgroundColor: '#384872' }}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: '#384872' }}>{step.title}</h4>
                      <p className="text-xs" style={{ color: '#6B7A99' }}>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conseils + Comment ça marche : côte à côte sur desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Conseils */}
            {details.tips.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2 md:mb-3 flex items-center gap-2" style={{ color: '#384872', fontFamily: 'Sora, sans-serif' }}>
                  <CheckCircle size={20} weight="duotone" style={{ color: '#72B76E' }} />
                  {t("agentPresentation.tipsForSuccess")}
                </h3>
                <ul className="space-y-2">
                  {details.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle size={16} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: '#72B76E' }} />
                      <span className="text-sm" style={{ color: '#6B7A99' }}>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Comment ça marche — adapté selon le type (coach formulaire vs training audio) */}
            {agent.formSchema ? (
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: '#EFF6FF' }}
              >
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{ color: '#384872' }}>
                  <FileText className="h-5 w-5" style={{ color: '#0EA5E9' }} />
                  {t("agentPresentation.howItWorks")}
                </h3>
                <ul className="space-y-1.5 text-sm" style={{ color: '#6B7A99' }}>
                  <li>• <SafeHtml html={t("agentPresentation.coachFillForm")} /></li>
                  <li>• <SafeHtml html={t("agentPresentation.coachAiAnalysis")} /></li>
                  <li>• <SafeHtml html={t("agentPresentation.coachStructuredResults")} /></li>
                  <li className="flex items-start gap-1.5">
                    <span>•</span>
                    <span>
                      <SafeHtml html={t("agentPresentation.coachChatQA")} />{" "}
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full align-middle mx-0.5" style={{ backgroundColor: '#F99002' }}>
                        <Send className="h-2.5 w-2.5 text-white" />
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            ) : (
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: '#FFF7ED' }}
              >
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{ color: '#384872' }}>
                  <Microphone size={20} weight="duotone" style={{ color: '#F99002' }} />
                  {t("agentPresentation.howItWorks")}
                </h3>
                <ul className="space-y-1.5 text-sm" style={{ color: '#6B7A99' }}>
                  <li>• <SafeHtml html={t("agentPresentation.oralSimulation")} /></li>
                  <li>• <SafeHtml html={t("agentPresentation.aiRespondsRealtime")} /></li>
                  <li className="flex items-start gap-1.5">
                    <span>•</span>
                    <span>
                      {t("agentPresentation.clickToStart")}{" "}
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-500 align-middle mx-0.5">
                        <Phone className="h-2.5 w-2.5 text-white" />
                      </span>
                      {" "}{t("agentPresentation.toStart")}{" "}
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 align-middle mx-0.5">
                        <MicOff className="h-2.5 w-2.5 text-white" />
                      </span>
                      {" "}{t("agentPresentation.forMic")}{" "}
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 align-middle mx-0.5">
                        <PhoneOff className="h-2.5 w-2.5 text-white" />
                      </span>
                      {" "}{t("agentPresentation.toEnd")}
                    </span>
                  </li>
                  <li>• <SafeHtml html={t("agentPresentation.detailedFeedback")} /></li>
                  <li>• <SafeHtml html={t("agentPresentation.retryAsNeeded")} /></li>
                </ul>
              </div>
            )}
          </div>

          {/* Bouton démarrer */}
          <div className="flex justify-center">
            {agent.isUnlocked !== false ? (
            <Button
              data-tour="agent-present-train"
              size="lg"
              className="px-6 py-4 text-base font-semibold gap-2"
              style={{ backgroundColor: '#F99002' }}
              onClick={onStartTraining}
            >
              <Play className="h-5 w-5" />
              {agent.simulatorType === "coach" ? t("agentPresentation.join") : t("agentPresentation.play")}
            </Button>
            ) : (
            <div className="px-6 py-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm text-center max-w-md">
              <Lock className="h-4 w-4 inline mr-1.5" />
              {t("agentPresentation.licenceRequired")}
            </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
